import { useCallback, useEffect } from 'react';
import usePortal from 'react-cool-portal';
import { Modal as BaseModal, Element } from '@zmedia/bm-soccer-ui/components';
import classnames from 'classnames';
import styles from './portal.module.scss';

interface IProps {
    clickOutsideToHide?: boolean;
    onHide?: () => void;
    fromBottom?: boolean;
}

export function useModal({
    clickOutsideToHide = true,
    fromBottom,
    ...options
}: IProps = {}) {
    const { Portal, isShow, show, hide, ...rest } = usePortal({
        defaultShow: false,
        internalShowHide: false,
        clickOutsideToHide,
        ...options,
    });

    const handleClick = (evt: React.MouseEvent) => {
        evt.preventDefault();
        evt.stopPropagation();
        if (clickOutsideToHide) {
            hide();
        }
    };

    useEffect(() => {
        const body = document.body;
        if (isShow) {
            body.classList.add('has-modal');
        } else {
            body.classList.remove('has-modal');
        }

        return () => body.classList.remove('has-modal');
    }, [isShow]);

    const Modal = useCallback(
        ({
            children,
            className,
            overlayClassName,
        }: {
            children: any;
            className?: string;
            overlayClassName?: string;
        }) => {
            if (!isShow) {
                return null;
            }

            return (
                <Portal>
                    <Element
                        className={classnames(
                            styles['bm-soccer-portal-modal'],
                            overlayClassName
                        )}
                        onClick={handleClick}
                    >
                        <BaseModal
                            isShow={isShow}
                            onClose={hide}
                            className={className}
                            clickOutsideToHide={clickOutsideToHide}
                            fromBottom={fromBottom}
                        >
                            {children}
                        </BaseModal>
                    </Element>
                </Portal>
            );
        },
        [isShow]
    );

    return { Modal, isShow, show, hide, ...rest };
}
